/**
 * The two views of a site's equipment, the drill-down tiles and the plant
 * board (piece 28; decisions 12560 to 12562): the status glyph rule, the
 * picture fallback, the tiles' search, the flow link's refusals and the view
 * choice. 🔨 The API and the screens read these same functions, so a status or
 * a refusal never differs between what the server answers and what is drawn.
 */
import type { AssetNodeType } from './hierarchy';
import { fill, t, type StringKey } from '../strings';
import type { IconMeaning } from '../icons';

// ---- The status glyph (decision 12562) ----

export const ASSET_STATUSES = ['Fault', 'Maintenance', 'WorkOrder', 'Disposed', 'None'] as const;
export type AssetStatus = (typeof ASSET_STATUSES)[number];

/** The facts the rule reads, as the tree read answers them per node. */
export interface AssetStatusFacts {
  disposed: boolean;
  /** Faults on the node with no clear yet. */
  openFaults: number;
  /** The soonest next due among the node's active Calendar schedules, `YYYY-MM-DD`, or null. */
  nextMaintenanceDueOn: string | null;
  /** A Work Order in Draft, Scheduled, Dispatched or In Progress names the node on a line. */
  openWorkOrder: boolean;
}

/**
 * 🔒 One rule (decision 12562): a disposed node reads Disposed whatever else it
 * carries; otherwise an open fault reads Fault, maintenance due on or before
 * `today` reads Maintenance, an open Work Order reads Work Order, and nothing
 * reads None. `today` is the organization's day as `YYYY-MM-DD`.
 */
export function assetStatusOf(facts: AssetStatusFacts, today: string): AssetStatus {
  if (facts.disposed) return 'Disposed';
  if (facts.openFaults > 0) return 'Fault';
  if (facts.nextMaintenanceDueOn !== null && facts.nextMaintenanceDueOn <= today) return 'Maintenance';
  if (facts.openWorkOrder) return 'WorkOrder';
  return 'None';
}

/** How much a status asks of a person: the corner reads the most pressing one beneath a node. */
const STATUS_WEIGHT: Readonly<Record<AssetStatus, number>> = { Fault: 4, Maintenance: 3, WorkOrder: 2, Disposed: 1, None: 0 };

export function worstAssetStatus(statuses: Iterable<AssetStatus>): AssetStatus {
  let worst: AssetStatus = 'None';
  for (const status of statuses) if (STATUS_WEIGHT[status] > STATUS_WEIGHT[worst]) worst = status;
  return worst;
}

/**
 * 🔒 The worst status beneath each node, its own left out (decision 12562): the
 * tile's corner and the board card's corner read it. A node with nothing
 * beneath it reads None.
 */
export function statusBeneath(rows: readonly { id: string; parentNodeId: string | null; status: AssetStatus }[]): Map<string, AssetStatus> {
  const children = new Map<string, string[]>();
  for (const row of rows) if (row.parentNodeId) children.set(row.parentNodeId, [...(children.get(row.parentNodeId) ?? []), row.id]);
  const own = new Map(rows.map((row) => [row.id, row.status]));
  const out = new Map<string, AssetStatus>();
  const walk = (id: string, seen: Set<string>): AssetStatus => {
    if (out.has(id)) return out.get(id)!;
    if (seen.has(id)) return 'None';
    seen.add(id);
    const below = (children.get(id) ?? []).map((child) => worstAssetStatus([own.get(child) ?? 'None', walk(child, seen)]));
    const worst = worstAssetStatus(below);
    out.set(id, worst);
    return worst;
  };
  for (const row of rows) walk(row.id, new Set());
  return out;
}

const STATUS_WORD: Readonly<Record<AssetStatus, StringKey>> = {
  Fault: 'asset.status.fault',
  Maintenance: 'asset.status.maintenance',
  WorkOrder: 'asset.status.workOrder',
  Disposed: 'asset.status.disposed',
  None: 'asset.status.none',
};

/** The status as a person reads it, a name in Title Case. */
export function assetStatusLabel(status: AssetStatus): string {
  return t(STATUS_WORD[status]);
}

/**
 * The colors each status draws in (decision 12562): red, amber and grey are the
 * error, warning and inactive groups; an open Work Order is navy, the job's own
 * color, outlined on the surface. None draws nothing on a tile.
 */
export const ASSET_STATUS_COLORS: Readonly<Record<AssetStatus, { foreground: string; background: string; outlined: boolean }>> = {
  Fault: { foreground: 'var(--status-error-text)', background: 'var(--status-error-bg)', outlined: false },
  Maintenance: { foreground: 'var(--status-warning-text)', background: 'var(--status-warning-bg)', outlined: false },
  WorkOrder: { foreground: 'var(--navy-primary)', background: 'var(--surface)', outlined: true },
  Disposed: { foreground: 'var(--status-inactive-text)', background: 'var(--status-inactive-bg)', outlined: false },
  None: { foreground: 'var(--status-good-text)', background: 'var(--status-good-bg)', outlined: false },
};

// ---- The picture (decision 12562, the owner's V2b) ----

export const ASSET_PICTURE_SOURCES = ['Own', 'CatalogPart', 'None'] as const;
export type AssetPictureSource = (typeof ASSET_PICTURE_SOURCES)[number];

export interface AssetPicture {
  source: AssetPictureSource;
  fileId: string | null;
}

/**
 * 🔒 The node's own picture, else its catalog part's where it is a Component,
 * else none, which the screen draws as the level's glyph.
 */
export function assetPictureOf(node: { imageFileId: string | null; nodeType: AssetNodeType; catalogImageFileId: string | null }): AssetPicture {
  if (node.imageFileId) return { source: 'Own', fileId: node.imageFileId };
  if (node.nodeType === 'Component' && node.catalogImageFileId) return { source: 'CatalogPart', fileId: node.catalogImageFileId };
  return { source: 'None', fileId: null };
}

/** 🔒 The glyph a node with no picture draws, on its level's colour (decision 12562). */
export const ASSET_LEVEL_GLYPH: Readonly<Record<AssetNodeType, IconMeaning>> = {
  AssetGroup: 'assetLevelGroup',
  FunctionGrouping: 'assetLevelFunction',
  ComponentGrouping: 'assetLevelComponentGroup',
  Component: 'assetLevelComponent',
};

// ---- The count of what stands inside (decision 12830) ----

/**
 * 🔒 The corner's count of what stands inside a node (decision 12830): a
 * Function or an Assembly holds parts, so it reads *N parts*; a System holds
 * Functions, so it reads *N inside*. Part is Component's synonym in prose.
 */
export function assetInsideText(nodeType: AssetNodeType, count: number): string {
  if (nodeType === 'FunctionGrouping' || nodeType === 'ComponentGrouping') return count === 1 ? t('asset.tiles.partsOne') : fill(t('asset.tiles.parts'), { count: String(count) });
  return fill(t('asset.tiles.inside'), { count: String(count) });
}

// ---- The tiles' search (decision 12560) ----

/**
 * 🔒 The nodes a search keeps: each whose name, tag or serial holds every typed
 * word, case aside, and every ancestor of one, so a match is drawn where it
 * stands. A blank search keeps everything, answered as null.
 */
export function assetSearchKeeps(rows: readonly { id: string; parentNodeId: string | null; name: string; assetTag: string | null; serialNumber: string | null }[], query: string): Set<string> | null {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return null;
  const parentOf = new Map(rows.map((row) => [row.id, row.parentNodeId]));
  const kept = new Set<string>();
  for (const row of rows) {
    const text = [row.name, row.assetTag ?? '', row.serialNumber ?? ''].join(' ').toLowerCase();
    if (!words.every((word) => text.includes(word))) continue;
    let at: string | null = row.id;
    while (at && !kept.has(at)) {
      kept.add(at);
      at = parentOf.get(at) ?? null;
    }
  }
  return kept;
}

// ---- The flow link (decision 12561) ----

/** A node as the link's check reads it: its parent, and its own stored site where it stands at the top. */
export interface FlowEnd {
  id: string;
  parentNodeId: string | null;
  siteId: string | null;
}

/** Two nodes are siblings when they share one parent, or both stand at the top of one named site (decisions 12561, T3818). */
function areSiblings(from: FlowEnd, to: FlowEnd): boolean {
  return from.parentNodeId !== null || to.parentNodeId !== null ? from.parentNodeId === to.parentNodeId : from.siteId !== null && from.siteId === to.siteId;
}

/**
 * 🔨 Why a flow link is refused, or null: a node to itself, two nodes that are
 * not siblings (one parent, or both at the top of one site, which two tops with no site are not), or a pair already
 * linked either way.
 */
export function flowLinkProblem(from: FlowEnd, to: FlowEnd, existing: readonly { fromNodeId: string; toNodeId: string; fromPortId?: string | null; toPortId?: string | null }[]): string | null {
  if (from.id === to.id) return 'A node cannot feed itself; pick another card.';
  if (!areSiblings(from, to)) return 'A link joins two nodes on the same level under one parent; these two stand on different levels.';
  // A port's arrow between the same two stands apart from a plain one (decision 12832).
  const plain = existing.filter((link) => !link.fromPortId && !link.toPortId);
  if (plain.some((link) => (link.fromNodeId === from.id && link.toNodeId === to.id) || (link.fromNodeId === to.id && link.toNodeId === from.id))) return 'These two are already linked; remove the arrow first to turn it round.';
  return null;
}

/** One arrow on the board, as the tree read answers it. */
export interface AssetFlowLinkAnswer {
  id: string;
  fromNodeId: string;
  toNodeId: string;
  /** 🔒 Decision 12832: the port each end runs through, null on a plain arrow between siblings. */
  fromPortId: string | null;
  toPortId: string | null;
}

// ---- The ports (decision 12832) ----

export const ASSET_PORT_DIRECTIONS = ['In', 'Out'] as const;
export type AssetPortDirection = (typeof ASSET_PORT_DIRECTIONS)[number];

export function isAssetPortDirection(value: unknown): value is AssetPortDirection {
  return typeof value === 'string' && (ASSET_PORT_DIRECTIONS as readonly string[]).includes(value);
}

/** One port on a Function, as the tree read and the node's page answer it. */
export interface AssetPortAnswer {
  id: string;
  assetNodeId: string;
  direction: AssetPortDirection;
  name: string | null;
  sortOrder: number;
}

/**
 * The port as a person reads it: its own name, else its direction and its place
 * among the Function's ports of that direction, *In 1* or *Out 2*.
 */
export function portLabel(port: Pick<AssetPortAnswer, 'id' | 'assetNodeId' | 'direction' | 'name'>, ports: readonly Pick<AssetPortAnswer, 'id' | 'assetNodeId' | 'direction' | 'sortOrder'>[]): string {
  if (port.name) return port.name;
  const same = ports.filter((one) => one.assetNodeId === port.assetNodeId && one.direction === port.direction).sort((a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id));
  const place = Math.max(1, same.findIndex((one) => one.id === port.id) + 1);
  return fill(t(port.direction === 'In' ? 'asset.port.inNumber' : 'asset.port.outNumber'), { number: String(place) });
}

/** One end of an arrow through a port: the node, its level and parent, and the port it runs through, or none. */
export interface PortEnd extends FlowEnd {
  nodeType: AssetNodeType;
  port: Pick<AssetPortAnswer, 'id' | 'assetNodeId' | 'direction'> | null;
}

/**
 * 🔨 Why an arrow naming a port is refused, or null (decision 12832). It takes
 * one of three shapes: a Function's In port to a part directly inside it, such a
 * part to one of its Out ports, or one Function's Out port to a sibling
 * Function's In port. `taken` holds the arrows already on either port: a port
 * carries one arrow on each side.
 */
export function portLinkProblem(from: PortEnd, to: PortEnd, taken: readonly Pick<AssetFlowLinkAnswer, 'fromPortId' | 'toPortId'>[]): string | null {
  if (from.port && from.port.assetNodeId !== from.id) return 'That port stands on another node; draw the arrow from the port itself.';
  if (to.port && to.port.assetNodeId !== to.id) return 'That port stands on another node; draw the arrow to the port itself.';
  if (from.port && to.port) {
    if (from.port.direction !== 'Out' || to.port.direction !== 'In') return 'An arrow between two Functions runs out of an Out port and into an In port.';
    if (from.id === to.id) return 'A Function cannot feed itself; pick another Function’s In port.';
    if (!areSiblings(from, to)) return 'A port links to a port of a Function beside it, under the same parent; these two stand under different parents.';
  } else if (from.port) {
    if (to.parentNodeId !== from.id) return to.nodeType === 'FunctionGrouping' ? 'A port links to a port, or to a part inside its own Function; pick one of the other card’s ports.' : 'A part links only to a port of the Function it stands in.';
    if (from.port.direction !== 'In') return 'Inside a Function, an In port feeds a part and a part feeds an Out port.';
  } else if (to.port) {
    if (from.parentNodeId !== to.id) return from.nodeType === 'FunctionGrouping' ? 'A port links to a port, or to a part inside its own Function; pick one of the other card’s ports.' : 'A part links only to a port of the Function it stands in.';
    if (to.port.direction !== 'Out') return 'Inside a Function, an In port feeds a part and a part feeds an Out port.';
  } else {
    return null;
  }
  if (from.port && taken.some((link) => link.fromPortId === from.port!.id)) return 'That port already carries an arrow on this side; remove it first.';
  if (to.port && taken.some((link) => link.toPortId === to.port!.id)) return 'That port already carries an arrow on this side; remove it first.';
  return null;
}

/**
 * The board's working order of one level (decision 12560): each node after the
 * nodes that feed it, ties by the order given; a loop the links cannot settle
 * falls back to that order.
 */
export function flowOrder(ids: readonly string[], links: readonly { fromNodeId: string; toNodeId: string }[]): string[] {
  const inLevel = new Set(ids);
  const feeds = links.filter((link) => inLevel.has(link.fromNodeId) && inLevel.has(link.toNodeId));
  const waiting = new Map(ids.map((id) => [id, feeds.filter((link) => link.toNodeId === id).length]));
  const out: string[] = [];
  const left = [...ids];
  while (left.length > 0) {
    const next = left.find((id) => (waiting.get(id) ?? 0) === 0) ?? left[0]!;
    left.splice(left.indexOf(next), 1);
    out.push(next);
    for (const link of feeds) if (link.fromNodeId === next) waiting.set(link.toNodeId, (waiting.get(link.toNodeId) ?? 1) - 1);
  }
  return out;
}

// ---- The view choice (decision 12560) ----

/** 🔒 List is offered on a Function's level alone (decision 12833); a kept List draws the Board on any other. */
export const ASSET_VIEWS = ['Tiles', 'Board', 'List'] as const;
export type AssetView = (typeof ASSET_VIEWS)[number];
/** 🔒 With no choice kept, the view is Tiles, on a desk and on a phone alike. */
export const ASSET_VIEW_DEFAULT: AssetView = 'Tiles';

export function isAssetView(value: unknown): value is AssetView {
  return typeof value === 'string' && (ASSET_VIEWS as readonly string[]).includes(value);
}

